import React, { useEffect, useState } from "react";
import { getSchedulingErrorMessage, searchCustomers } from "../../../api/schedulingApi";
import { Alert, Field, inputClass } from "./ui";

export const emptyContact = () => ({
  mode: "existing", person: null, query: "", name: "", gender: "", phone: "",
  relationship: "mother", is_primary: false, is_guardian: false,
});

export function contactPayload(value) {
  return {
    ...(value.mode === "existing" ? { contact_person_id: value.person.id } : {
      new_contact: {
        name: value.name.trim(),
        ...(value.gender ? { gender: value.gender } : {}),
        phone: value.phone.trim() || null,
      },
    }),
    relationship: value.relationship.trim(),
    is_primary: value.is_primary,
    is_guardian: value.is_guardian,
  };
}

export default function CustomerContactEditor({ botId, customerId, value, onChange, editing = false, disabled = false }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (key, next) => onChange({ ...value, [key]: next });
  useEffect(() => {
    let active = true;
    setResults([]);
    setError("");
    if (editing || value.mode !== "existing" || value.person || !value.query.trim()) {
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const rows = await searchCustomers(botId, { q: value.query.trim(), limit: 20 });
        if (active) setResults((rows || []).filter((row) => row.id !== customerId));
      } catch (err) {
        if (active) setError(getSchedulingErrorMessage(err, "Could not search contact people."));
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [botId, customerId, editing, value.mode, value.person, value.query]);

  return <fieldset disabled={disabled} className="space-y-3">
    {!editing && <>
      <Field label="Contact person">
        <select className={inputClass} value={value.mode} onChange={(event) => onChange({ ...value, mode: event.target.value, person: null, query: "" })}>
          <option value="existing">Select existing customer</option>
          <option value="new">Create a new contact person</option>
        </select>
      </Field>
      {value.mode === "existing" ? <>
        <Field label="Search contact person" hint="Search by name, phone, email or registration number.">
          <input className={inputClass} value={value.query} onChange={(event) => onChange({ ...value, query: event.target.value, person: null })} />
        </Field>
        {value.person && <p className="rounded-lg bg-cyan-50 p-2 text-sm text-cyan-900">Selected: {value.person.name || "Unnamed"} · {value.person.phone || "No phone"}</p>}
        {loading && <p role="status" className="text-sm text-slate-500">Searching contacts...</p>}
        {!loading && !error && value.query.trim() && !value.person && !results.length && <p className="text-sm text-slate-500">No matching contacts. You can create a new person.</p>}
        <Alert>{error}</Alert>
        {!!results.length && !value.person && <ul aria-label="Contact search results" className="max-h-40 overflow-y-auto rounded-lg border border-slate-200">
          {results.map((person) => <li key={person.id}><button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onChange({ ...value, person })}>
            {person.name || "Unnamed"} · {person.phone || "No phone"}{person.registration_number ? ` · ${person.registration_number}` : ""}
          </button></li>)}
        </ul>}
      </> : <>
        <Field label="Contact name"><input required maxLength={200} className={inputClass} value={value.name} onChange={(event) => set("name", event.target.value)} /></Field>
        <Field label="Contact gender"><select className={inputClass} value={value.gender} onChange={(event) => set("gender", event.target.value)}>
          <option value="">Not specified</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="nonbinary">Non-binary</option>
          <option value="other">Other</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select></Field>
        <Field label="Contact phone" hint="Optional. Family members may share a number."><input maxLength={30} className={inputClass} value={value.phone} onChange={(event) => set("phone", event.target.value)} /></Field>
      </>}
    </>}
    <Field label="Relationship" hint="The contact person's relationship to the patient, for example mother, father, son, spouse or guardian. When editing, keep the original direction shown above.">
      <input required maxLength={30} className={inputClass} value={value.relationship} onChange={(event) => set("relationship", event.target.value)} />
    </Field>
    <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={value.is_primary} onChange={(event) => set("is_primary", event.target.checked)} />Primary contact</label>
    <p className="text-xs text-slate-500">Choosing a primary contact replaces the previous primary. A primary contact is not required.</p>
    <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={value.is_guardian} onChange={(event) => set("is_guardian", event.target.checked)} />Guardian designation</label>
    <p className="text-xs text-slate-500">A recorded relationship or guardian designation does not authorize consent signing or redirect messages.</p>
  </fieldset>;
}
